import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { StepperContextService } from '../../../../../../services/stepper-context.service';
import { StepperAnswersService } from '../../../../../../services/stepper-answers.service';
import { AboutYouSteps } from '../../../../../../enums/about-you-steps.enum';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { SharedDropdownModule } from '../../../../../../../../shared/shared-dropdown.module';
import { Subject, takeUntil } from 'rxjs';
import { LocationService } from '../../../../../../../../core/services/location.service';
import { CoreModule, NotificationService } from '../../../../../../../../core/core.module';
import { AddressValidationService } from '../../../../../../services/address-validation.service';

export enum CurrentAddressInputs {
  Address1 = 'Address',
  City = 'City',
  State = 'State',
  Zip = 'Zip'
}

@Component({
  selector: 'lgv-borrower-current-address',
  standalone: true,
  imports: [CommonModule, SharedDropdownModule,
    MatDividerModule, MatButtonModule, MatInputModule,
    ReactiveFormsModule
  ],
  templateUrl: './borrower-current-address.component.html',
  styleUrls: ['./borrower-current-address.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BorrowerCurrentAddressComponent {
  CurrentAddressInputs = CurrentAddressInputs
  prepopulated = false
  formGroup = this.fb.group({
    [CurrentAddressInputs.Address1]: ['', Validators.required],
    [CurrentAddressInputs.City]: ['', [Validators.required]],
    [CurrentAddressInputs.State]: ['', [Validators.required]],
    [CurrentAddressInputs.Zip]: ['', [Validators.required,
    Validators.minLength(5),
    Validators.maxLength(5)]]
  });
  onDestroy$ = new Subject();
  isLoading = false

  @Output() stepChange = new EventEmitter<any>();
  @Output() sectionChange = new EventEmitter<any>();

  get zipCodeField() {
    return this.formGroup.get(CurrentAddressInputs.Zip);
  }
  get stateField() {
    return this.formGroup.get(CurrentAddressInputs.State);
  }

  constructor(
    public contextService: StepperContextService,
    public answerService: StepperAnswersService,
    public addressService: AddressValidationService,
    private fb: FormBuilder,
  ) { }

  ngAfterViewInit() {
    let currentAnswers = this.answerService.getAnswer(AboutYouSteps.BorrowerCurrentAddress)
    if (currentAnswers && Object.keys(currentAnswers).length > 0) {
      this.prepopulated = true
      this.formGroup.setValue({
        [CurrentAddressInputs.Address1]: currentAnswers[CurrentAddressInputs.Address1] as string ?? '',
        [CurrentAddressInputs.City]: currentAnswers[CurrentAddressInputs.City] as string ?? '',
        [CurrentAddressInputs.State]: currentAnswers[CurrentAddressInputs.State] as string ?? '',
        [CurrentAddressInputs.Zip]: currentAnswers[CurrentAddressInputs.Zip] as string ?? '',
      });
    }
  }

  onNext() {
    this.answerService.setAnswer(AboutYouSteps.BorrowerCurrentAddress,
      {
        [CurrentAddressInputs.Address1]: this.formGroup.get(CurrentAddressInputs.Address1).value,
        [CurrentAddressInputs.City]: this.formGroup.get(CurrentAddressInputs.City).value,
        [CurrentAddressInputs.State]: this.formGroup.get(CurrentAddressInputs.State).value,
        [CurrentAddressInputs.Zip]: this.formGroup.get(CurrentAddressInputs.Zip).value,
      })
    this.goToStep(AboutYouSteps.BorrowerCitizenship)
  }

  onBack() {
    if (!this.formGroup.invalid) {
      this.answerService.setAnswer(AboutYouSteps.BorrowerCurrentAddress,
        {
          [CurrentAddressInputs.Address1]: this.formGroup.get(CurrentAddressInputs.Address1).value,

          [CurrentAddressInputs.City]: this.formGroup.get(CurrentAddressInputs.City).value,
          [CurrentAddressInputs.State]: this.formGroup.get(CurrentAddressInputs.State).value,
          [CurrentAddressInputs.Zip]: this.formGroup.get(CurrentAddressInputs.Zip).value,
        })
    }
    this.goToStep(AboutYouSteps.BorrowerMilitary)
  }

  goToSection(section, step) {
    this.sectionChange.emit([section, step]);
  }

  goToStep(step) {
    this.stepChange.emit(step);
  }

  isNextDisabled() {
    return this.formGroup.invalid || this.isLoading
  }

  ngOnDestroy(): void {
    this.onDestroy$.complete();
    this.onDestroy$.unsubscribe();
  }
}
